<script module lang="ts">
  import type { ClickEvent } from '#app/types/declarations/svelte'
  import type { SerializedEntity } from '#entities/lib/entities'

  export type SerializedHomonym = SerializedEntity & {
    isMerging?: boolean
    merged?: boolean
    merge?: (e?: ClickEvent) => Promise<void>
  }
</script>

<script lang="ts">
  import { map, partition } from 'lodash-es'
  import { icon } from '#app/lib/icons'
  import Homonym from '#entities/components/deduplicate/homonym.svelte'
  import { findExactMatches, getHomonymsEntities, preselectLikelyDuplicates } from '#entities/components/deduplicate/lib/helpers'
  import EntityListRow from '#entities/components/layouts/entity_list_row.svelte'
  import Spinner from '#general/components/spinner.svelte'
  import SearchMaintenanceFlash from '#search/components/search_maintenance_flash.svelte'
  import type { EntityUri } from '#server/types/entity'
  import { I18n, i18n } from '#user/lib/i18n'
  import { mainUserHasDataadminAccess } from '#user/lib/serialized_main_user'

  const {
    entity,
    standalone = false,
  }: {
    entity: SerializedEntity
    standalone?: boolean
  } = $props()

  let homonyms: SerializedHomonym[] = $state([])
  let invHomonyms: SerializedHomonym[] = $state([])
  let selectedHomonymsUris: EntityUri[] = $state([])
  let wdExactMatches: SerializedHomonym[] | undefined = $state()
  let invExactMatches: SerializedHomonym[] | undefined = $state()

  async function getHomonymsPromise () {
    homonyms = await getHomonymsEntities(entity)
    ;({ wdExactMatches, invExactMatches } = findExactMatches(entity, homonyms))
    selectedHomonymsUris = preselectLikelyDuplicates({ entity, wdExactMatches, invExactMatches }) || []
    // Display the preselected homonyms first
    const [ preselected, notPreselected ] = partition(homonyms, isSelectedHomonym)
    homonyms = preselected.concat(notPreselected)
    invHomonyms = homonyms.filter(homonym => !homonym.isWikidataEntity)
  }

  function isSelectedHomonym (homonym: SerializedHomonym) {
    return !homonym.isMerging && !homonym.merged && selectedHomonymsUris.includes(homonym.uri)
  }

  function selectAllExactMatches () {
    selectedHomonymsUris = map(invExactMatches, 'uri')
  }
  function selectAll () {
    selectedHomonymsUris = map(invHomonyms, 'uri')
  }
  function unselectAll () {
    selectedHomonymsUris = []
  }

  function mergeSelectedSuggestions () {
    const homonymsToMerge = homonyms.filter(isSelectedHomonym)

    async function mergeSequentially () {
      const nextSelectedHomonym = homonymsToMerge.shift()
      if (nextSelectedHomonym == null) return
      // The merge function is set on the entity by the MergeAction component (via the `bind:merge` below)
      await nextSelectedHomonym.merge()
      return mergeSequentially()
    }

    // Merge 3 at a time
    mergeSequentially()
    mergeSequentially()
    mergeSequentially()
  }
</script>

{#if standalone}
  <div class="entity-info-wrapper">
    <EntityListRow {entity} />
  </div>
{/if}

{#if mainUserHasDataadminAccess()}
  {#await getHomonymsPromise()}
    <div class="loading-wrapper">
      <p class="loading">{i18n('Looking for duplicates…')}
        <Spinner center={true} />
      </p>
    </div>
  {:then}
    <div class="deduplicate-homonyms" class:standalone>
      <h4>
        {@html icon('compress')}
        {I18n('merge homonyms')}
      </h4>

      <div class="merge-homonyms-controls">
        <button
          class="tiny-button"
          onclick={selectAllExactMatches}
          aria-controls="selectable-homonyms"
          disabled={invExactMatches.length === 0}
        >
          {@html icon('check-square')}
          {I18n('select all local exact matches')}
          <span class="count">({invExactMatches.length})</span>
        </button>
        <button
          class="tiny-button"
          onclick={selectAll}
          aria-controls="selectable-homonyms"
          disabled={invHomonyms.length === 0}
        >
          {@html icon('check-square')}
          {I18n('select all local entities')}
          <span class="count">({invHomonyms.length})</span>
        </button>
        <button
          class="tiny-button"
          onclick={unselectAll}
          aria-controls="selectable-homonyms"
        >
          {@html icon('square')}
          {I18n('unselect all')}
        </button>
        <button
          class="tiny-button"
          onclick={mergeSelectedSuggestions}
          aria-controls="selectable-homonyms"
          disabled={selectedHomonymsUris.length === 0}
        >
          {@html icon('compress')}
          {I18n('merge selected suggestions')}
          <span class="count">({selectedHomonymsUris.length})</span>
        </button>
      </div>
      <ul id="selectable-homonyms">
        {#each homonyms as homonym, i (homonym.uri)}
          <Homonym {entity} bind:homonym={homonyms[i]} bind:selectedHomonymsUris />
        {:else}
          <p class="no-results">{I18n('no homonym found')}</p>
        {/each}
      </ul>
      <SearchMaintenanceFlash />
    </div>
  {/await}
{/if}

<style lang="scss">
  @import "general/scss/utils";
  .loading-wrapper, .entity-info-wrapper{
    @include display-flex(row, center, center);
  }
  .entity-info-wrapper{
    max-inline-size: 50em;
    margin: 1em auto;
  }
  .merge-homonyms-controls button{
    margin: 0.2em;
    .count{
      color: white;
      // Prevent button width change on count change
      display: inline-block;
      min-inline-size: 2em;
      text-align: center;
    }
  }
  .deduplicate-homonyms{
    @include display-flex(column, center);
    background-color: $off-white;
    padding: 1em;
    margin: 0.5em 0;
  }
  #selectable-homonyms{
    @include display-flex(row, flex-start, center, wrap);
    margin: 1em auto;
  }
  .standalone{
    :global(li){
      margin: 0.5em;
    }
  }
  .no-results{
    margin: 1em;
    padding: 1em;
  }
  /* Smaller screens */
  @media screen and (width < $smaller-screen){
    .merge-homonyms-controls{
      @include display-flex(column, center);
    }
  }
  /* Small screens */
  @media screen and (width < $small-screen){
    button{
      margin-block-end: 0.5em;
    }
  }
</style>
